import { useState, useEffect, useCallback, useRef } from "react";
import { DiscardConfirm } from "@/components/DiscardConfirm";
import { API } from "@/App";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { Shield, Plus, Edit2, Trash2, Lock, Info } from "lucide-react";

// Fixed catalogue — mirrors backend/routes/roles.py::ALL_PAGES / PERM_LEVELS.
// Split page lists per module (kept as top-level flat arrays to avoid nested
// array patterns that trip Emergent's visual-edits babel plugin).
const CUSTOMER_RELATIONS_PAGES = [
  { id: "today", label: "Today" },
  { id: "upcoming", label: "Upcoming" },
  { id: "history", label: "History" },
  { id: "reception", label: "Reception" },
];

const VEHICLES_RENAULT_PAGES = [
  { id: "vehicles_renault_vehicle", label: "Vehicle" },
  { id: "vehicles_renault_customer", label: "Customer" },
  { id: "vehicles_renault_insurance", label: "Insurance" },
  { id: "vehicles_renault_dates_programs", label: "Dates & Programs" },
  { id: "vehicles_renault_documents", label: "Documents" },
  { id: "vehicles_renault_dealer", label: "Dealer" },
];
const VEHICLES_OTHERBRANDS_PAGES = [
  { id: "vehicles_otherbrands_vehicle", label: "Vehicle" },
  { id: "vehicles_otherbrands_customer", label: "Customer" },
  { id: "vehicles_otherbrands_insurance", label: "Insurance" },
  { id: "vehicles_otherbrands_dates_programs", label: "Dates & Programs" },
  { id: "vehicles_otherbrands_documents", label: "Documents" },
];
const PARTS_PAGES = [{ id: "parts", label: "Parts" }];
const BODYSHOP_PAGES = [{ id: "bodyshop", label: "Bodyshop" }];
const MECHANICAL_PAGES = [{ id: "mechanical", label: "Mechanical" }];
const INSURANCE_PAGES = [{ id: "insurance", label: "Insurance" }];
const HR_PAGES = [{ id: "hr", label: "HR" }];

const SETTINGS_PAGES = [{ id: "settings", label: "Settings" }];
const DATA_QUALITY_PAGES = [{ id: "data_quality", label: "Data Quality" }];

const MODULES = [
  { id: "customer_relations", label: "Customer Relations", pages: CUSTOMER_RELATIONS_PAGES },
  {
    id: "vehicles",
    label: "Vehicles",
    groups: [
      { id: "renault", label: "Renault", pages: VEHICLES_RENAULT_PAGES },
      { id: "otherbrands", label: "Other Brands", pages: VEHICLES_OTHERBRANDS_PAGES },
    ],
  },
  { id: "parts", label: "Parts", pages: PARTS_PAGES },
  { id: "bodyshop", label: "Bodyshop", pages: BODYSHOP_PAGES },
  { id: "mechanical", label: "Mechanical", pages: MECHANICAL_PAGES },
  { id: "insurance", label: "Insurance", pages: INSURANCE_PAGES },
  { id: "hr", label: "HR", pages: HR_PAGES },
  { id: "data_quality", label: "Data Quality", pages: DATA_QUALITY_PAGES },
  { id: "settings", label: "Settings", pages: SETTINGS_PAGES },
];

// Dashboard is a special module — one on/off checkbox per tab (no 4-level columns).
const DASHBOARD_TABS = [
  { id: "dp_dashboard", label: "DP Dashboard" },
  { id: "cr", label: "Customer Relations" },
  { id: "vehicles", label: "Vehicles" },
  { id: "parts", label: "Parts" },
  { id: "bodyshop", label: "Bodyshop" },
  { id: "mechanical", label: "Mechanical" },
  { id: "insurance", label: "Insurance" },
  { id: "hr", label: "HR" },
];

// Flat list of every page id across all modules (used to seed defaults).
const ALL_PAGES = [
  ...CUSTOMER_RELATIONS_PAGES,
  ...VEHICLES_RENAULT_PAGES,
  ...VEHICLES_OTHERBRANDS_PAGES,
  ...PARTS_PAGES,
  ...BODYSHOP_PAGES,
  ...MECHANICAL_PAGES,
  ...INSURANCE_PAGES,
  ...HR_PAGES,
  ...SETTINGS_PAGES,
];

const PERM_LEVELS = [
  { id: "view", label: "View" },
  { id: "update", label: "Update" },
  { id: "edit", label: "Edit" },
  { id: "master", label: "Master" },
];

const emptyDashboard = () =>
  Object.fromEntries(DASHBOARD_TABS.map((t) => [t.id, false]));

const emptyPermissions = () => ({
  ...Object.fromEntries(
    ALL_PAGES.map((p) => [
      p.id,
      Object.fromEntries(PERM_LEVELS.map((l) => [l.id, false])),
    ])
  ),
  dashboard: emptyDashboard(),
});

// Shared 4-level checkbox table used by every module block (and sub-groups).
const PermMatrixTable = ({ pages, permissions, readOnly, onToggle }) => (
  <Card className="border border-gray-200 rounded-sm shadow-none overflow-hidden">
    <Table>
      <TableHeader>
        <TableRow className="table-header bg-gray-50">
          <TableHead className="text-xs">Particulars</TableHead>
          {PERM_LEVELS.map((lvl) => (
            <TableHead key={lvl.id} className="text-xs text-center w-24">
              {lvl.label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {pages.map((page) => (
          <TableRow key={page.id} data-testid={`matrix-row-${page.id}`}>
            <TableCell className="font-medium">{page.label}</TableCell>
            {PERM_LEVELS.map((lvl) => {
              // Settings row is intentionally all-or-nothing — only Master is
              // togglable. Backend enforces the same rule on write.
              const isSettingsLocked =
                page.id === "settings" && lvl.id !== "master";
              // Data Quality only reads and links out — nothing is edited here,
              // so only View is meaningful.
              const isDataQualityLocked =
                page.id === "data_quality" && lvl.id !== "view";
              const isDisabled = readOnly || isSettingsLocked || isDataQualityLocked;
              return (
                <TableCell key={lvl.id} className="text-center">
                  <Checkbox
                    checked={!!permissions[page.id]?.[lvl.id]}
                    onCheckedChange={() =>
                      !isDisabled && onToggle(page.id, lvl.id)
                    }
                    disabled={isDisabled}
                    className={`rounded-sm ${
                      isSettingsLocked || isDataQualityLocked
                        ? "cursor-not-allowed !bg-gray-300 !border-gray-300 !opacity-100"
                        : ""
                    }`}
                    data-testid={`perm-${page.id}-${lvl.id}`}
                  />
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Card>
);

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [savingRole, setSavingRole] = useState(false);
  const savingRoleRef = useRef(false);
  const [deletingRole_, setDeletingRole_] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null); // null = create mode
  const [deletingRole, setDeletingRole] = useState(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const formSnapshotRef = useRef(null);

  // Snapshot the form when the Role Builder opens — dirty = form changed.
  useEffect(() => {
    if (dialogOpen) formSnapshotRef.current = JSON.stringify(form);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen]);
  const [form, setForm] = useState({
    name: "",
    description: "",
    permissions: emptyPermissions(),
  });

  const fetchRoles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/roles`, { withCredentials: true });
      setRoles(res.data || []);
    } catch {
      toast.error("Failed to load roles");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const openNew = () => {
    setEditingRole(null);
    setForm({ name: "", description: "", permissions: emptyPermissions() });
    setDialogOpen(true);
  };

  const openEdit = (role) => {
    setEditingRole(role);
    // Merge with empty template — protects against roles that predate a page addition.
    const perms = { ...emptyPermissions(), ...(role.permissions || {}) };
    for (const page of ALL_PAGES) {
      perms[page.id] = {
        ...emptyPermissions()[page.id],
        ...(perms[page.id] || {}),
      };
    }
    perms.dashboard = { ...emptyDashboard(), ...(perms.dashboard || {}) };
    setForm({
      name: role.name,
      description: role.description || "",
      permissions: perms,
    });
    setDialogOpen(true);
  };

  const togglePerm = (pageId, levelId) => {
    setForm((f) => ({
      ...f,
      permissions: {
        ...f.permissions,
        [pageId]: {
          ...f.permissions[pageId],
          [levelId]: !f.permissions[pageId][levelId],
        },
      },
    }));
  };

  const toggleDashTab = (tabId) => {
    setForm((f) => ({
      ...f,
      permissions: {
        ...f.permissions,
        dashboard: {
          ...f.permissions.dashboard,
          [tabId]: !f.permissions.dashboard[tabId],
        },
      },
    }));
  };

  const handleSave = async () => {
    if (savingRoleRef.current) return;
    const name = form.name.trim();
    if (!name) {
      toast.error("Role name is required");
      return;
    }
    setSavingRole(true);
    savingRoleRef.current = true;
    try {
      if (editingRole) {
        await axios.put(
          `${API}/roles/${editingRole.role_id}`,
          form,
          { withCredentials: true }
        );
        toast.success("Role updated");
      } else {
        await axios.post(`${API}/roles`, form, { withCredentials: true });
        toast.success("Role created");
      }
      setDialogOpen(false);
      fetchRoles();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save role");
    } finally {
      setSavingRole(false);
      savingRoleRef.current = false;
    }
  };

  const handleDelete = async () => {
    if (deletingRole_) return;
    setDeletingRole_(true);
    try {
      await axios.delete(`${API}/roles/${deletingRole.role_id}`, {
        withCredentials: true,
      });
      toast.success("Role deleted");
      setDeletingRole(null);
      fetchRoles();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete role");
    } finally {
      setDeletingRole_(false);
    }
  };

  const readOnly = editingRole?.is_system === true;

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="roles-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-black text-white rounded-sm flex items-center justify-center">
            <Shield className="w-6 h-6" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter uppercase">
              Roles
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage roles and permissions
            </p>
          </div>
        </div>

        <Button
          className="bg-black text-white hover:bg-gray-800 rounded-sm text-xs uppercase tracking-wider"
          onClick={openNew}
          data-testid="new-role-btn"
        >
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
          New Role
        </Button>
      </div>

      {/* Roles Table */}
      <Card className="border border-gray-200 rounded-sm shadow-none overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="table-header bg-gray-50">
              <TableHead className="text-xs">Role Name</TableHead>
              <TableHead className="text-xs">Description</TableHead>
              <TableHead className="text-xs w-32">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-gray-400 py-8">
                  Loading...
                </TableCell>
              </TableRow>
            ) : roles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-gray-400 py-8">
                  No roles yet — click &quot;New Role&quot; to create one.
                </TableCell>
              </TableRow>
            ) : (
              roles.map((role) => (
                <TableRow
                  key={role.role_id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => openEdit(role)}
                  data-testid={`role-row-${role.role_id}`}
                >
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <span>{role.name}</span>
                      {role.is_system && (
                        <Badge className="bg-black text-white rounded-sm text-[10px] uppercase tracking-wider">
                          <Lock
                            className="w-3 h-3 mr-1"
                            strokeWidth={2}
                          />
                          System
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {role.description || "—"}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-sm"
                        onClick={() => openEdit(role)}
                        data-testid={`edit-role-${role.role_id}`}
                        title={role.is_system ? "View" : "Edit"}
                      >
                        <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                      {!role.is_system && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeletingRole(role)}
                          data-testid={`delete-role-${role.role_id}`}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Role Builder Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="rounded-sm max-w-3xl max-h-[90vh] overflow-hidden !flex !flex-col"
          data-testid="role-dialog"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          closeClassName="p-1 transition-colors hover:bg-red-600 hover:text-white hover:opacity-100"
          onCloseClick={(e) => {
            if (JSON.stringify(form) !== formSnapshotRef.current) {
              e.preventDefault();
              setDiscardConfirmOpen(true);
            }
          }}
        >
          <DialogHeader className="shrink-0 border-b border-gray-200 pb-3">
            <DialogTitle className="font-heading font-bold text-xl tracking-tight uppercase">
              {editingRole
                ? readOnly
                  ? "View Role"
                  : "Edit Role"
                : "New Role"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-4 overflow-y-auto flex-1">
            {/* Name */}
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                Role Name *
              </Label>
              <Input
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                placeholder="e.g. Receptionist"
                className="rounded-sm"
                disabled={readOnly}
                data-testid="role-name-input"
              />
            </div>

            {/* Description */}
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                Description
              </Label>
              <Textarea
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                placeholder="What this role can do..."
                className="rounded-sm"
                rows={2}
                disabled={readOnly}
                data-testid="role-description-input"
              />
            </div>

            {/* Legend */}
            <div className="p-3 border border-gray-200 rounded-sm bg-gray-50 text-xs space-y-1.5">
              <p>
                <span className="font-bold">View</span> — see the page, change nothing.
              </p>
              <p>
                <span className="font-bold">Update</span> — advance the process (outcomes, N-1, flags, receive a vehicle).
              </p>
              <p>
                <span className="font-bold">Edit</span> — change existing data, delete entries.
              </p>
              <p>
                <span className="font-bold">Master</span> — override locks (edit past / locked records).
              </p>
            </div>

            {/* Matrix — grouped by module. Add a new module block by extending
                the MODULES catalogue at the top of this file. Destructuring
                the pages/groups arrays out avoids nested member-expression
                patterns that trip Emergent's visual-edits babel plugin. */}
            <div className="space-y-6">
              {MODULES.map(({ id: modId, label: modLabel, pages: modPages, groups: modGroups }) => (
                <div key={modId}>
                  <h3
                    className="font-heading font-bold text-sm uppercase tracking-tight mb-2 flex items-center gap-1.5"
                    data-testid={`module-heading-${modId}`}
                  >
                    {modLabel}
                    {modId === "vehicles" && (
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info
                              className="w-3.5 h-3.5 text-gray-400 cursor-help"
                              data-testid="vehicles-cascade-info"
                            />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs" data-testid="vehicles-cascade-tooltip">
                            Master on any sub-page grants full access to all
                            sub-pages in that group (Renault or Other Brands).
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </h3>
                  {modGroups ? (
                    <div className="space-y-4">
                      {modGroups.map(({ id: grpId, label: grpLabel, pages: grpPages }) => (
                        <div key={grpId}>
                          <h4
                            className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-1.5"
                            data-testid={`subgroup-heading-${modId}-${grpId}`}
                          >
                            {grpLabel}
                          </h4>
                          <PermMatrixTable
                            pages={grpPages}
                            permissions={form.permissions}
                            readOnly={readOnly}
                            onToggle={togglePerm}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <PermMatrixTable
                      pages={modPages}
                      permissions={form.permissions}
                      readOnly={readOnly}
                      onToggle={togglePerm}
                    />
                  )}
                </div>
              ))}

              {/* Dashboard — checklist module: one on/off checkbox per tab. */}
              <div>
                <h3
                  className="font-heading font-bold text-sm uppercase tracking-tight mb-2"
                  data-testid="module-heading-dashboard"
                >
                  Dashboard
                </h3>
                <Card className="border border-gray-200 rounded-sm shadow-none overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header bg-gray-50">
                        <TableHead className="text-xs">Particulars</TableHead>
                        <TableHead className="text-xs text-center w-24">
                          Access
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {DASHBOARD_TABS.map(({ id: tabId, label: tabLabel }) => (
                        <TableRow
                          key={tabId}
                          data-testid={`matrix-row-dashboard-${tabId}`}
                        >
                          <TableCell className="font-medium">
                            {tabLabel}
                          </TableCell>
                          <TableCell className="text-center">
                            <Checkbox
                              checked={!!form.permissions.dashboard?.[tabId]}
                              onCheckedChange={() =>
                                !readOnly && toggleDashTab(tabId)
                              }
                              disabled={readOnly}
                              className="rounded-sm"
                              data-testid={`perm-dashboard-${tabId}`}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            </div>

            {readOnly && (
              <p className="text-xs text-gray-500 italic flex items-center gap-1">
                <Lock className="w-3 h-3" strokeWidth={2} />
                This is a built-in system role. It cannot be edited or deleted.
              </p>
            )}
          </div>

          <DialogFooter className="pt-4">
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => setDialogOpen(false)}
              data-testid="role-cancel-btn"
            >
              {readOnly ? "Close" : "Cancel"}
            </Button>
            {!readOnly && (
              <Button
                className="bg-black text-white hover:bg-gray-800 rounded-sm"
                onClick={handleSave}
                disabled={savingRole}
                data-testid="role-save-btn"
              >
                {editingRole ? "Update" : "Create"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DiscardConfirm
        open={discardConfirmOpen}
        onOpenChange={setDiscardConfirmOpen}
        onDiscard={() => {
          setDiscardConfirmOpen(false);
          setDialogOpen(false);
        }}
      />

      {/* Delete confirm */}
      <Dialog
        open={!!deletingRole}
        onOpenChange={(o) => !o && setDeletingRole(null)}
      >
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-xl tracking-tight uppercase">
              Delete Role?
            </DialogTitle>
          </DialogHeader>
          <div className="pt-4">
            <p className="text-sm text-gray-600">
              This will permanently delete{" "}
              <strong>{deletingRole?.name}</strong>. Users still assigned to
              this role will keep the label, but no permissions will apply.
            </p>
          </div>
          <DialogFooter className="pt-4">
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => setDeletingRole(null)}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 text-white hover:bg-red-700 rounded-sm"
              onClick={handleDelete}
              disabled={deletingRole_}
              data-testid="role-confirm-delete-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Roles;
